import {
  Activity,
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  BadgeDollarSign,
  Ban,
  Beaker,
  Boxes,
  Check,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CircleGauge,
  Clipboard,
  Clock,
  Cloud,
  Database,
  ExternalLink,
  EyeOff,
  FileText,
  Globe2,
  Info,
  KeyRound,
  Layers3,
  Link2,
  MinusCircle,
  Play,
  Plus,
  Radar,
  RefreshCw,
  Route,
  Save,
  Search,
  ServerCog,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Terminal,
  Undo2,
  X,
  type LucideIcon,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type Dispatch, type FormEvent, type ReactNode, type SetStateAction } from "react";
import {
  type ReasoningEffort,
  type ReasoningPolicy,
} from "@ftr/core";
import {
  loadAdminData,
  loadOpenCodeConfig,
  mutate,
  ISO_COUNTRIES,
  type AdminData,
  type GeoPolicyRow,
  type ModelRow,
  type OpportunityRow,
  type RequestLogItem,
  type RouteRow,
  type SourceRow,
} from "./api";

type Section =
  | "overview"
  | "models"
  | "sources"
  | "routes"
  | "countries"
  | "opportunities"
  | "keys"
  | "logs"
  | "budget"
  | "config"
  | "playground"
  | "audits";

const sections: Array<{ id: Section; label: string; icon: LucideIcon }> = [
  { id: "overview", label: "总览看板", icon: CircleGauge },
  { id: "models", label: "逻辑模型", icon: Boxes },
  { id: "sources", label: "上游来源", icon: Cloud },
  { id: "routes", label: "来源调度", icon: Route },
  { id: "countries", label: "国家策略", icon: Globe2 },
  { id: "opportunities", label: "试用候选箱", icon: Radar },
  { id: "keys", label: "Gateway Keys", icon: KeyRound },
  { id: "logs", label: "决策解释器", icon: FileText },
  { id: "budget", label: "预算与账本", icon: BadgeDollarSign },
  { id: "config", label: "OpenCode 配置", icon: ServerCog },
  { id: "playground", label: "网关调试台", icon: Terminal },
  { id: "audits", label: "审计记录", icon: Clipboard },
];

function describeError(error: unknown): string {
  if (error instanceof Error && error.message.trim()) return error.message;
  return "操作失败，请稍后重试或查看浏览器控制台";
}

function safeRandomUUID(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    try {
      return crypto.randomUUID();
    } catch {
      // Fallback
    }
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getPresetModelSuggestions(preset: string): string[] {
  switch (preset) {
    case "nvidia":
      return [
        "deepseek-ai/deepseek-v4-flash",
        "deepseek-ai/deepseek-r1",
        "meta/llama-3.3-70b-instruct",
        "nvidia/llama-3.1-nemotron-70b-instruct",
      ];
    case "openrouter":
      return [
        "deepseek/deepseek-chat",
        "deepseek/deepseek-r1",
        "meta-llama/llama-3.3-70b-instruct",
        "anthropic/claude-3.5-sonnet",
      ];
    case "google":
      return [
        "gemini-2.5-flash",
        "gemini-2.5-pro",
        "gemini-2.0-flash",
      ];
    case "deepseek":
      return ["deepseek-chat", "deepseek-reasoner"];
    case "moonshot":
      return ["moonshot-v1-8k", "moonshot-v1-32k", "moonshot-v1-128k"];
    case "amd":
      return ["meta/llama-3.3-70b-instruct", "deepseek-ai/deepseek-r1"];
    default:
      return [];
  }
}

function timeAgo(timestamp: number | null): string {
  if (!timestamp) return "从未";
  const minutes = Math.max(0, Math.round((Date.now() - timestamp) / 60000));
  if (minutes < 60) return `${minutes} 分钟前`;
  if (minutes < 1440) return `${Math.round(minutes / 60)} 小时前`;
  return `${Math.round(minutes / 1440)} 天前`;
}

function reasoningPolicyFromModel(model: ModelRow): ReasoningPolicy {
  try {
    const capabilities = JSON.parse(model.capabilities_json) as { reasoning?: ReasoningPolicy };
    return capabilities.reasoning ?? { mode: "inherit" };
  } catch {
    return { mode: "inherit" };
  }
}

// 复制按钮即时反馈：点击后按钮本体 2s 内切换为 "✓ 已复制"
function useCopyButton(timeoutMs = 2000): [boolean, (text: string) => Promise<boolean>] {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  const copy = async (text: string): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      if (timer.current !== null) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setCopied(false), timeoutMs);
      return true;
    } catch {
      return false;
    }
  };

  return [copied, copy];
}

function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "green" | "amber" | "blue" | "neutral";
}) {
  return <span className={`badge badge-${tone}`}>{children}</span>;
}

function Modal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section className="modal" role="dialog" aria-modal="true" aria-labelledby="dialog-title">
        <header>
          <h2 id="dialog-title">{title}</h2>
          <button className="icon-button" onClick={onClose} aria-label="关闭">
            <X size={20} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

export function App() {
  const [section, setSection] = useState<Section>("overview");
  const [data, setData] = useState<AdminData | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<{ message: string; tone: "success" | "warn" | "error" } | null>(null);

  // Modals
  const [modal, setModal] = useState<"model" | "source" | "route" | "policy" | "key" | "friction" | null>(null);
  const [editingPolicyId, setEditingPolicyId] = useState("");
  const [createdKey, setCreatedKey] = useState("");
  const [keySavedConfirmed, setKeySavedConfirmed] = useState(false);
  const [keyCopied, copyKey] = useCopyButton();
  const [newModelId, setNewModelId] = useState("");
  const [modelReasoningMode, setModelReasoningMode] = useState<ReasoningPolicy["mode"]>("inherit");
  const [modelReasoningEffort, setModelReasoningEffort] = useState<ReasoningEffort>("high");

  // Friction modal state
  const [frictionAction, setFrictionAction] = useState<{
    title: string;
    description: string;
    onConfirm: () => Promise<void>;
  } | null>(null);

  const refresh = async () => {
    setBusy(true);
    setLoadError(null);
    try {
      setData(await loadAdminData());
    } catch (error) {
      setLoadError(describeError(error));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const currentSection = sections.find((item) => item.id === section);
  const title = currentSection?.label ?? "总览看板";

  const showNotice = (message: string, tone: "success" | "warn" | "error" = "success") => {
    setNotice({ message, tone });
    window.setTimeout(() => setNotice(null), 3500);
  };

  const saveModel = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const row: ModelRow = {
      id: newModelId,
      display_name: String(form.get("displayName")),
      context_window: Number(form.get("contextWindow")),
      max_output_tokens: Number(form.get("maxOutputTokens")),
      capabilities_json: JSON.stringify({
        text: true,
        streaming: true,
        tools: true,
        json: true,
        reasoning: {
          mode: modelReasoningMode,
          ...(modelReasoningMode === "enabled" ? { effort: modelReasoningEffort } : {}),
        },
      }),
      enabled: 1,
    };
    if (!data?.demo) {
      try {
        await mutate("/models", "POST", row);
      } catch (error) {
        showNotice(describeError(error), "error");
        return;
      }
    }
    setData((current) => (current ? { ...current, models: [...current.models, row] } : current));
    setModal(null);
    showNotice("逻辑模型已注册生效");
  };

  const saveSource = async (sourceData: Record<string, unknown>) => {
    const sourcePayload: Record<string, unknown> = {
      id: String(sourceData.id),
      name: String(sourceData.name),
      preset: String(sourceData.preset),
      protocol: sourceData.protocol,
      baseUrl: String(sourceData.baseUrl),
      tier: sourceData.tier,
      endpointCountry: String(sourceData.endpointCountry),
      geoPolicyId: String(sourceData.geoPolicyId),
      credential: String(sourceData.credential),
      ...(sourceData.quota ? { quota: sourceData.quota } : {}),
    };

    // 步骤 1：保存来源（凭据在此处经 AES-256-GCM 加密后写入 D1）
    // 失败时抛出，交由表单层显示行内错误并保持弹窗打开供修正重试
    if (!data?.demo) {
      try {
        await mutate("/sources", "POST", sourcePayload);
      } catch (error) {
        throw new Error(describeError(error));
      }
    }

    const sourceRow: SourceRow = {
      id: String(sourceData.id),
      name: String(sourceData.name),
      preset: String(sourceData.preset),
      protocol: sourceData.protocol as "chat" | "responses",
      base_url: String(sourceData.baseUrl),
      tier: sourceData.tier as "free" | "paid",
      endpoint_country: String(sourceData.endpointCountry),
      enabled: 1,
      geo_policy_id: String(sourceData.geoPolicyId),
    };

    let newModelRow: ModelRow | null = null;
    let newRouteRow: RouteRow | null = null;

    // 步骤 2/3（可选）：快捷绑定逻辑模型与调度路由
    if (sourceData.attachModel && sourceData.upstreamModelId) {
      const targetModelId = String(sourceData.targetLogicalModelId).trim();
      const upstreamModelId = String(sourceData.upstreamModelId).trim();
      const manualPriority = Number(sourceData.manualPriority || 50);

      if (sourceData.isNewModel) {
        newModelRow = {
          id: targetModelId,
          display_name: String(sourceData.newModelDisplayName || targetModelId).trim(),
          context_window: 128000,
          max_output_tokens: 8192,
          capabilities_json: JSON.stringify({
            text: true,
            streaming: true,
            tools: true,
            json: true,
            reasoning: { mode: "inherit" },
          }),
          enabled: 1,
        };
      }

      const routeId = safeRandomUUID();
      const routePayload = {
        id: routeId,
        logicalModelId: targetModelId,
        sourceId: sourceRow.id,
        upstreamModelId,
        manualPriority,
      };

      if (!data?.demo) {
        try {
          if (newModelRow) await mutate("/models", "POST", newModelRow);
          await mutate("/routes", "POST", routePayload);
        } catch (error) {
          // 来源已在服务端落库，必须同步到界面，
          // 否则会出现"界面显示没保存、实际已保存"的不一致状态
          setData((current) =>
            current ? { ...current, sources: [...current.sources, sourceRow] } : current,
          );
          setModal(null);
          showNotice(
            `来源已保存，但模型/路由绑定失败：${describeError(error)}。请到「来源调度」页面手动完成绑定。`,
            "error",
          );
          return;
        }
      }

      newRouteRow = {
        id: routeId,
        logical_model_id: targetModelId,
        source_id: sourceRow.id,
        upstream_model_id: upstreamModelId,
        manual_priority: manualPriority,
        source_name: sourceRow.name,
        tier: sourceRow.tier,
        endpoint_country: sourceRow.endpoint_country,
        scoreBreakdown: {
          priorityWeight: (100 - manualPriority) * 0.45,
          successWeight: 29.5,
          quotaWeight: 14.0,
          latencyWeight: 9.0,
          totalScore: (100 - manualPriority) * 0.45 + 52.5,
        },
      };
    }

    setData((current) => {
      if (!current) return current;
      return {
        ...current,
        sources: [...current.sources, sourceRow],
        ...(newModelRow ? { models: [...current.models, newModelRow] } : {}),
        ...(newRouteRow ? { routes: [...current.routes, newRouteRow] } : {}),
      };
    });

    setModal(null);
    if (newRouteRow) {
      showNotice(`来源与模型路由 [${newRouteRow.logical_model_id}] 均已成功配置生效`);
    } else {
      showNotice("来源已加密写入边缘存储 (凭据仅写不读)");
    }
  };

  const createKey = async (payload: { name: string; rpm: number; concurrency: number; allowedModels: string[] }) => {
    let value: string;
    if (data?.demo) {
      value = `ftr_live_${safeRandomUUID().replaceAll("-", "").slice(0, 8)}.${safeRandomUUID().replaceAll("-", "")}`;
    } else {
      try {
        const result = await mutate<{ value: string }>("/keys", "POST", {
          name: payload.name,
          requestsPerMinute: payload.rpm,
          maxConcurrency: payload.concurrency,
          allowedModels: payload.allowedModels,
        });
        value = result.value;
      } catch (error) {
        showNotice(describeError(error), "error");
        return;
      }
    }
    setCreatedKey(value);
    setKeySavedConfirmed(false);
  };

  const saveRoute = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const row = {
      id: safeRandomUUID(),
      logicalModelId: String(form.get("logicalModelId")),
      sourceId: String(form.get("sourceId")),
      upstreamModelId: String(form.get("upstreamModelId")),
      manualPriority: Number(form.get("manualPriority")),
    };
    if (!data?.demo) {
      try {
        await mutate("/routes", "POST", row);
      } catch (error) {
        showNotice(describeError(error), "error");
        return;
      }
    }
    const source = data?.sources.find((item) => item.id === row.sourceId);
    if (source)
      setData((current) =>
        current
          ? {
              ...current,
              routes: [
                ...current.routes,
                {
                  id: row.id,
                  logical_model_id: row.logicalModelId,
                  source_id: row.sourceId,
                  upstream_model_id: row.upstreamModelId,
                  manual_priority: row.manualPriority,
                  source_name: source.name,
                  tier: source.tier,
                  endpoint_country: source.endpoint_country,
                  scoreBreakdown: {
                    priorityWeight: (100 - row.manualPriority) * 0.45,
                    successWeight: 29.5,
                    quotaWeight: 14.0,
                    latencyWeight: 9.0,
                    totalScore: (100 - row.manualPriority) * 0.45 + 52.5,
                  },
                },
              ],
            }
          : current,
      );
    setModal(null);
    showNotice("同模型调度绑定已更新");
  };

  if (!data)
    return (
      <div className="loading">
        <div className="brand-mark">
          <Layers3 size={24} />
        </div>
        <div>
          <RefreshCw className="spin" size={18} style={{ verticalAlign: "middle", marginRight: 8 }} />
          <span role={loadError ? "alert" : "status"}>{loadError ?? "正在连接 Cloudflare 边缘环境与 D1 状态账本..."}</span>
          {loadError && <button className="primary-button" onClick={() => void refresh()} disabled={busy}>重试连接</button>}
        </div>
      </div>
    );

  return (
    <div className="app-shell">
      {loadError && <div role="alert" className="notice">刷新失败：{loadError}；当前显示上次加载的数据。</div>}
      <aside className="sidebar">
        <div className="brand">
          <div className="brand-mark">
            <Layers3 size={22} />
          </div>
          <div>
            <strong>FreeToken</strong>
            <span>Router Console</span>
          </div>
        </div>
        <nav aria-label="主要导航">
          {sections.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              className={section === id ? "active" : ""}
              onClick={() => setSection(id)}
            >
              <Icon size={18} />
              <span>{label}</span>
              {id === "opportunities" && data.dashboard.pendingOpportunities > 0 && (
                <em>{data.dashboard.pendingOpportunities}</em>
              )}
            </button>
          ))}
        </nav>
        <div className="sidebar-foot">
          <ShieldCheck size={16} />
          <span>Cloudflare Access</span>
          <Badge tone="green">已保护</Badge>
        </div>
      </aside>

      <main>
        <header className="topbar">
          <div className="topbar-info">
            <span className="eyebrow">EDGE CONTROL PLANE · ACCESS PROTECTED</span>
            <h1>{title}</h1>
          </div>
          <div className="top-actions">
            <div className="system-status-indicator">
              <span className="pulse-dot" />
              <span>状态优先系统</span>
            </div>
            {data.demo && <Badge tone="amber">演示模式</Badge>}
            <button className="ghost-button" onClick={() => void refresh()} disabled={busy}>
              <RefreshCw size={15} className={busy ? "spin" : ""} />
              刷新
            </button>
          </div>
        </header>

        <div className="content">
          {section === "overview" && (
            <OverviewSection
              data={data}
              onNavigate={(s) => setSection(s)}
              onCheckPolicy={(policyId) => {
                setEditingPolicyId(policyId);
                setModal("policy");
              }}
            />
          )}
          {section === "models" && <ModelsSection data={data} onAdd={() => {
            setNewModelId("");
            setModelReasoningMode("inherit");
            setModelReasoningEffort("high");
            setModal("model");
          }} />}
          {section === "sources" && (
            <SourcesSection
              data={data}
              notify={showNotice}
              onAdd={() => setModal("source")}
            />
          )}
          {section === "routes" && (
            <RoutesSection
              data={data}
              setData={setData}
              notify={showNotice}
              onAdd={() => setModal("route")}
            />
          )}
          {section === "countries" && (
            <CountriesSection
              data={data}
              setData={setData}
              notify={showNotice}
              onEdit={(id) => {
                setEditingPolicyId(id);
                setModal("policy");
              }}
            />
          )}
          {section === "opportunities" && (
            <OpportunitiesSection
              data={data}
              setData={setData}
              notify={showNotice}
              onRequestFriction={(title, desc, action) => {
                setFrictionAction({ title, description: desc, onConfirm: action });
                setModal("friction");
              }}
            />
          )}
          {section === "keys" && (
            <KeysSection
              data={data}
              setData={setData}
              notify={showNotice}
              onAdd={() => {
                setCreatedKey("");
                setModal("key");
              }}
              onRequestFriction={(title, desc, action) => {
                setFrictionAction({ title, description: desc, onConfirm: action });
                setModal("friction");
              }}
            />
          )}
          {section === "logs" && <LogsSection data={data} />}
          {section === "budget" && (
            <BudgetSection
              data={data}
              setData={setData}
              notify={showNotice}
              onRequestFriction={(title, desc, action) => {
                setFrictionAction({ title, description: desc, onConfirm: action });
                setModal("friction");
              }}
            />
          )}
          {section === "config" && <ConfigSection data={data} notify={showNotice} />}
          {section === "playground" && <PlaygroundSection data={data} notify={showNotice} />}
          {section === "audits" && <AuditsSection data={data} />}
        </div>
      </main>

      {notice && (
        <div className={`toast toast-${notice.tone}`}>
          {notice.tone === "success" && <CircleCheck size={18} />}
          {notice.tone === "warn" && <CircleAlert size={18} />}
          {notice.tone === "error" && <Ban size={18} />}
          <span>{notice.message}</span>
        </div>
      )}

      {/* Modals */}
      {modal === "model" && (
        <Modal title="注册逻辑模型" onClose={() => setModal(null)}>
          <form className="form-grid" onSubmit={(event) => void saveModel(event)}>
            <label>
              逻辑模型 ID (model)
              <input
                name="id"
                required
                placeholder="deepseek-v4-flash"
                pattern="[a-z0-9._-]+"
                value={newModelId}
                onChange={(event) => setNewModelId(event.target.value.trim())}
              />
              <small>客户端请求必须精确匹配，网关绝不自动跨模型切换</small>
            </label>
            <label>
              显示名称
              <input name="displayName" required placeholder="DeepSeek Coder" />
              <small>控制台可读标识</small>
            </label>
            <label>
              上下文窗口 (Tokens)
              <input name="contextWindow" type="number" min="1024" defaultValue="128000" required />
            </label>
            <label>
              最大输出 Tokens
              <input name="maxOutputTokens" type="number" min="1" defaultValue="8192" required />
            </label>
            <label>
              模型思考模式
              <select
                value={modelReasoningMode}
                onChange={(event) => setModelReasoningMode(event.target.value as ReasoningPolicy["mode"])}
              >
                <option value="inherit">不注入（继承上游默认）</option>
                <option value="disabled">固定关闭思考</option>
                <option value="enabled">固定开启思考</option>
              </select>
              <small>每次请求会按逻辑模型自动读取该配置，无需客户端重复传参</small>
            </label>
            {modelReasoningMode === "enabled" && (
              <label>
                固定推理强度
                <select
                  value={modelReasoningEffort}
                  onChange={(event) => setModelReasoningEffort(event.target.value as ReasoningEffort)}
                >
                  <option value="minimal">minimal</option>
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                  <option value="max">max</option>
                </select>
              </label>
            )}
            <div className="form-actions">
              <button type="button" className="ghost-button" onClick={() => setModal(null)}>
                取消
              </button>
              <button className="primary-button">
                <Save size={16} />
                保存逻辑模型
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "source" && (
        <Modal title="新增上游来源 (预设感知动态表单)" onClose={() => setModal(null)}>
          <SourceDynamicForm
            data={data}
            onCancel={() => setModal(null)}
            onSubmit={async (src) => {
              try {
                await saveSource(src);
              } catch (error) {
                showNotice(describeError(error), "error");
                throw error;
              }
            }}
          />
        </Modal>
      )}

      {modal === "route" && (
        <Modal title="添加同模型调度绑定" onClose={() => setModal(null)}>
          <form className="form-grid" onSubmit={(event) => void saveRoute(event)}>
            <label>
              目标逻辑模型
              <select name="logicalModelId">
                {data.models
                  .filter((m) => m.enabled)
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.display_name} ({m.id})
                    </option>
                  ))}
              </select>
            </label>
            <label>
              上游服务提供源
              <select name="sourceId">
                {data.sources
                  .filter((s) => s.enabled)
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} [{s.tier.toUpperCase()}]
                    </option>
                  ))}
              </select>
            </label>
            <label className="wide">
              准确上游 Model ID
              <input
                name="upstreamModelId"
                required
                placeholder="deepseek-ai/deepseek-v4-flash-0731"
              />
            </label>
            <div className="preset-callout preset-callout-info">
              <Info size={16} />
              <div>
                <strong>自动思考策略：</strong>转发时按目标逻辑模型的“思考模式”配置自动注入，
                不依赖上游 Model ID 的命名规则。
              </div>
            </div>
            <label>
              人工基础优先级 (1-100，越小越优先)
              <input name="manualPriority" type="number" min="1" max="100" defaultValue="50" />
              <small>此项占综合评分的 45%，最终顺序还将结合成功率、剩余额度与延迟负载综合评定</small>
            </label>
            <div className="form-actions">
              <button type="button" className="ghost-button" onClick={() => setModal(null)}>
                取消
              </button>
              <button className="primary-button">
                <Route size={16} />
                确认添加
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "policy" && (
        <PolicyEditModal
          data={data}
          policyId={editingPolicyId}
          onClose={() => setModal(null)}
          onSaved={(updated) => {
            setData((current) =>
              current
                ? {
                    ...current,
                    geoPolicies: current.geoPolicies.map((p) => (p.id === updated.id ? updated : p)),
                  }
                : current,
            );
            setModal(null);
            showNotice("国家合规策略已刷新核验周期并生效");
          }}
        />
      )}

      {modal === "key" && (
        <Modal title="签发 Gateway Key (凭据只写不读)" onClose={() => (createdKey && !keySavedConfirmed ? null : setModal(null))}>
          {createdKey ? (
            <div className="secret-result">
              <ShieldCheck size={36} />
              <h3>Gateway Key 已成功签发</h3>
              <p>
                <strong>唯一可见机会</strong>：网关仅保存该 Key 的 HMAC-SHA256 摘要，关闭后不可再次读取。请立刻复制！
              </p>
              <code>{createdKey}</code>
              <label className="checkbox-label" style={{ margin: "16px 0", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={keySavedConfirmed}
                  onChange={(e) => setKeySavedConfirmed(e.target.checked)}
                />
                <span style={{ fontSize: "0.82rem", color: "var(--text-strong)" }}>
                  我已复制并妥善保存在安全的密码管理器中
                </span>
              </label>
              <div style={{ display: "flex", gap: "8px" }}>
                <button
                  className="secondary-button"
                  onClick={async () => {
                  const ok = await copyKey(createdKey);
                  showNotice(ok ? "已复制至剪贴板" : "复制失败，请手动选择复制", ok ? "success" : "error");
                }}
                >
                  {keyCopied ? <Check size={15} /> : <Clipboard size={15} />}
                  {keyCopied ? "已复制" : "复制密钥"}
                </button>
                <button
                  className="primary-button"
                  disabled={!keySavedConfirmed}
                  onClick={() => {
                    setModal(null);
                    showNotice("凭据已关闭明文显示，列表仅显示脱敏前缀");
                  }}
                >
                  已安全保存，关闭弹窗
                </button>
              </div>
            </div>
          ) : (
            <KeyCreateForm
              models={data.models}
              onCancel={() => setModal(null)}
              onSubmit={(p) => void createKey(p)}
            />
          )}
        </Modal>
      )}

      {modal === "friction" && frictionAction && (
        <Modal title="高风险敏感操作确认" onClose={() => setModal(null)}>
          <div style={{ padding: "20px 24px" }}>
            <div className="friction-box">
              <h4>{frictionAction.title}</h4>
              <p>{frictionAction.description}</p>
            </div>
            <div className="form-actions">
              <button className="ghost-button" onClick={() => setModal(null)}>
                放弃操作
              </button>
              <button
                className="danger-button"
                style={{ padding: "8px 16px" }}
                onClick={async () => {
                  await frictionAction.onConfirm();
                  setModal(null);
                }}
              >
                确认执行高风险变更
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ========================================================
// 1. Overview Section (状态优先于配置：异常第一原则)
// ========================================================
function OverviewSection({
  data,
  onNavigate,
  onCheckPolicy,
}: {
  data: AdminData;
  onNavigate: (s: Section) => void;
  onCheckPolicy: (id: string) => void;
}) {
  // Compute anomalies
  const anomalies: Array<{
    id: string;
    title: string;
    desc: string;
    type: "danger" | "warn";
    icon: LucideIcon;
    actionText: string;
    onClick: () => void;
  }> = [];

  // Check circuit breaker / failed sources
  data.sources.forEach((s) => {
    if (s.health?.circuitState === "open" || s.health?.anomaly === "circuit_open") {
      anomalies.push({
        id: `source-circuit-${s.id}`,
        title: `上游来源熔断中: ${s.name}`,
        desc: `检测到连续错误，当前已熔断降级。最近报错: ${s.health.lastError || "429/超时"}`,
        type: "danger",
        icon: Ban,
        actionText: "检查来源",
        onClick: () => onNavigate("sources"),
      });
    }
  });

  // Check stale or expiring policies (<= 7 days)
  data.geoPolicies.forEach((p) => {
    if (p.restricted) {
      const staleDays = Math.floor((Date.now() - p.verified_at) / 86400000);
      const remainingDays = p.valid_days - staleDays;
      if (remainingDays <= 0) {
        anomalies.push({
          id: `policy-expired-${p.id}`,
          title: `国家合规策略已超期: ${p.name}`,
          desc: `超过 ${p.valid_days} 天未核验，按设计原则已 Fail-Closed 阻断调度`,
          type: "danger",
          icon: AlertTriangle,
          actionText: "核验策略",
          onClick: () => onCheckPolicy(p.id),
        });
      } else if (remainingDays <= 7) {
        anomalies.push({
          id: `policy-warning-${p.id}`,
          title: `国家合规策略即将超期: ${p.name}`,
          desc: `剩余 ${remainingDays} 天有效期，请提前重新核对供应商官方条款`,
          type: "warn",
          icon: Clock,
          actionText: "延长核验",
          onClick: () => onCheckPolicy(p.id),
        });
      }
    }
  });

  // Check pending opportunities
  if (data.dashboard.pendingOpportunities > 0) {
    anomalies.push({
      id: "opportunities-pending",
      title: `${data.dashboard.pendingOpportunities} 个新试用机会等待人工审核`,
      desc: "自动化抓取已就绪，需管理员确认使用条款合规后方可接入路由",
      type: "warn",
      icon: Radar,
      actionText: "进入候选箱",
      onClick: () => onNavigate("opportunities"),
    });
  }

  // Budget warning
  if (data.budget.remainingUsd < data.budget.globalLimitUsd * 0.15 && data.budget.globalLimitUsd > 0) {
    anomalies.push({
      id: "budget-low",
      title: `全局付费预算告警: 剩余 $${data.budget.remainingUsd.toFixed(2)}`,
      desc: "可用预算已低于 15%，超额后付费保底将熔断拦截",
      type: "warn",
      icon: BadgeDollarSign,
      actionText: "预算设置",
      onClick: () => onNavigate("budget"),
    });
  }

  const stats = [
    { label: "注册逻辑模型", value: data.dashboard.models, meta: `${data.dashboard.routes} 条路由链路`, icon: Boxes, tone: "blue" },
    { label: "活跃免费来源", value: data.dashboard.freeSources, meta: "严格最先尝试", icon: Sparkles, tone: "green" },
    { label: "付费保底备用", value: data.dashboard.paidSources, meta: `$${data.budget.globalLimitUsd.toFixed(2)} 全局月限`, icon: BadgeDollarSign, tone: "amber" },
    { label: "试用机会候选", value: data.dashboard.pendingOpportunities, meta: "人工审核机制", icon: Radar, tone: "purple" },
  ];

  return (
    <>
      {/* 异常优先聚合看板 */}
      <section className={`anomaly-banner ${anomalies.length === 0 ? "all-clean" : ""}`}>
        <div className="anomaly-header">
          <div className="anomaly-title">
            {anomalies.length > 0 ? (
              <>
                <AlertTriangle size={18} color="var(--amber)" />
                <h3>异常优先看板：当前有 {anomalies.length} 项系统状态需关注</h3>
              </>
            ) : (
              <>
                <CircleCheck size={18} color="var(--lime)" />
                <h3>系统状态全部健康：所有调度链路与国家策略均正常生效</h3>
              </>
            )}
          </div>
          <span style={{ fontSize: "0.75rem", color: "var(--muted)", fontFamily: "JetBrains Mono" }}>
            FAIL-CLOSED READY
          </span>
        </div>
        {anomalies.length > 0 && (
          <div className="anomaly-items-grid">
            {anomalies.map((item) => (
              <div
                key={item.id}
                className={`anomaly-card ${item.type === "danger" ? "danger" : ""}`}
                onClick={item.onClick}
              >
                <item.icon size={18} color={item.type === "danger" ? "var(--danger)" : "var(--amber)"} />
                <div>
                  <strong>{item.title}</strong>
                  <p>{item.desc}</p>
                </div>
                <span className="jump-btn">
                  <ChevronRight size={14} />
                </span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* 统计指标卡片 */}
      <section className="stat-grid">
        {stats.map(({ label, value, meta, icon: Icon, tone }) => (
          <article className="stat-card" key={label}>
            <div className={`stat-icon tone-${tone}`}>
              <Icon size={20} />
            </div>
            <span>{label}</span>
            <strong>{value}</strong>
            <small>{meta}</small>
          </article>
        ))}
      </section>

      {/* 拆分面板 */}
      <section className="split-grid">
        <article className="panel">
          <header>
            <div>
              <span className="eyebrow">ROUTING EXPLANATION</span>
              <h2>实时同模型调度链</h2>
            </div>
            <button className="text-button" onClick={() => onNavigate("routes")}>
              完整调度详情 <ChevronRight size={15} />
            </button>
          </header>
          <div className="route-stack">
            {data.routes.slice(0, 5).map((route, index) => (
              <div className="route-line" key={route.id}>
                <span className="route-index">{String(index + 1).padStart(2, "0")}</span>
                <div>
                  <strong>{route.logical_model_id}</strong>
                  <small>
                    {route.source_name} · <code>{route.upstream_model_id}</code>
                  </small>
                </div>
                <Badge tone={route.tier === "free" ? "green" : "amber"}>
                  {route.tier === "free" ? "FREE TIER" : "PAID GUARD"}
                </Badge>
                <Badge tone="blue">{route.endpoint_country}</Badge>
              </div>
            ))}
          </div>
        </article>

        <article className="panel">
          <header>
            <div>
              <span className="eyebrow">COMPLIANCE & BUDGET</span>
              <h2>合规边界与预算双态</h2>
            </div>
            <ShieldCheck color="var(--lime)" size={20} />
          </header>
          <div className="compliance-list">
            <div>
              <span className="status-dot ok" />
              <p>
                <strong>合规规则不能被优先级覆盖</strong>
                <small>即使人工优先级 P1，若请求国未在供应商白名单内将一律返回 451</small>
              </p>
            </div>
            <div>
              <span className="status-dot ok" />
              <p>
                <strong>凭据只写不读机制</strong>
                <small>上游密文 AES-256 存盘，控制台关闭后永久掩码，杜绝窃取风险</small>
              </p>
            </div>
            <div>
              <span className="status-dot warn" />
              <p>
                <strong>预算预留制 (Reserved In-Flight)</strong>
                <small>
                  已结算 ${data.budget.settledUsd.toFixed(2)} | 预留中 ${data.budget.reservedUsd.toFixed(2)}
                </small>
              </p>
            </div>
          </div>
          <button className="secondary-button full" onClick={() => onNavigate("budget")}>
            <BadgeDollarSign size={15} />
            查看预算账本双态拆解
          </button>
        </article>
      </section>
    </>
  );
}

// ========================================================
// 2. Models Section (逻辑模型)
// ========================================================
function ModelsSection({ data, onAdd }: { data: AdminData; onAdd: () => void }) {
  const [search, setSearch] = useState("");
  const filtered = data.models.filter(
    (m) =>
      m.id.toLowerCase().includes(search.toLowerCase()) ||
      m.display_name.toLowerCase().includes(search.toLowerCase()),
  );
  const status = (model: ModelRow): { label: string; tone: "green" | "amber" | "neutral" } => {
    switch (model.operational_status) {
      case "healthy": return { label: "健康可用", tone: "green" };
      case "degraded": return { label: "部分降级", tone: "amber" };
      case "circuit_open": return { label: "全部熔断", tone: "neutral" };
      case "half_open": return { label: "半开探测", tone: "amber" };
      case "unverified": return { label: "等待验证", tone: "amber" };
      case "unconfigured": return { label: "未配置路由", tone: "neutral" };
      case "disabled": return { label: "已禁用", tone: "neutral" };
      default: return model.enabled ? { label: "等待验证", tone: "amber" } : { label: "已禁用", tone: "neutral" };
    }
  };

  return (
    <section className="panel">
      <header className="section-header">
        <div>
          <span className="eyebrow">LOGICAL MODEL REGISTRY</span>
          <h2>逻辑模型列表</h2>
          <p>网关调度只在同一逻辑模型的多来源间切换，绝不跨模型降级或回退。</p>
        </div>
        <button className="primary-button" onClick={onAdd}>
          <Plus size={16} />
          注册逻辑模型
        </button>
      </header>
      <div className="filter-bar">
        <div className="search-input-wrap">
          <Search size={16} />
          <input
            placeholder="搜索模型 ID 或显示名称..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>模型 ID</th>
              <th>上下文长度</th>
              <th>最大输出 Token</th>
              <th>支持特性</th>
              <th>状态</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((model) => {
              const currentStatus = status(model);
              return <tr key={model.id}>
                <td>
                  <strong>{model.display_name}</strong>
                  <small>
                    <code>{model.id}</code>
                  </small>
                </td>
                <td>{model.context_window.toLocaleString()}</td>
                <td>{model.max_output_tokens.toLocaleString()}</td>
                <td>
                  <div className="badge-row">
                    <Badge tone="blue">STREAM</Badge>
                    <Badge>TOOLS</Badge>
                    <Badge>JSON</Badge>
                    {reasoningPolicyFromModel(model).mode === "enabled" && <Badge tone="amber">思考开启</Badge>}
                    {reasoningPolicyFromModel(model).mode === "disabled" && <Badge>思考关闭</Badge>}
                  </div>
                </td>
                <td>
                  <Badge tone={currentStatus.tone}>
                    {currentStatus.label}
                  </Badge>
                  <small>{model.open_route_count ?? 0}/{model.route_count ?? 0} 条路由熔断</small>
                </td>
              </tr>;
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ========================================================
// 3. Sources Section (来源列表与加权分量分解)
// ========================================================
function SourcesSection({
  data,
  notify,
  onAdd,
}: {
  data: AdminData;
  notify: (msg: string, tone?: "success" | "warn" | "error") => void;
  onAdd: () => void;
}) {
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState<"all" | "free" | "paid">("all");
  const [testingId, setTestingId] = useState("");

  const filtered = data.sources.filter((s) => {
    const matchSearch =
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.preset.toLowerCase().includes(search.toLowerCase()) ||
      s.base_url.toLowerCase().includes(search.toLowerCase());
    const matchTier = tierFilter === "all" || s.tier === tierFilter;
    return matchSearch && matchTier;
  });

  const testSource = async (s: SourceRow) => {
    setTestingId(s.id);
    try {
      const res = data.demo
        ? { ok: true, status: 200, latencyMs: 82 }
        : await mutate<{ ok: boolean; status: number; latencyMs: number }>(`/sources/${s.id}/test`, "POST", {});
      notify(
        res.ok
          ? `[${s.name}] 连通测试通过 · HTTP ${res.status} · 延迟 ${res.latencyMs} ms`
          : `测试失败: HTTP ${res.status}`,
        res.ok ? "success" : "error",
      );
    } catch (e) {
      notify(e instanceof Error ? e.message : "请求超时", "error");
    } finally {
      setTestingId("");
    }
  };

  return (
    <section className="panel">
      <header className="section-header">
        <div>
          <span className="eyebrow">UPSTREAM INVENTORY</span>
          <h2>上游供应商来源</h2>
          <p>来源机密凭据严格加密保存，前端界面不提供任何“查看明文”入口，确保后置零泄漏。</p>
        </div>
        <button className="primary-button" onClick={onAdd}>
          <Plus size={16} />
          新增来源
        </button>
      </header>
      <div className="filter-bar">
        <div className="search-input-wrap">
          <Search size={16} />
          <input
            placeholder="搜索来源名称或端点 Base URL..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="filter-chips">
          <button
            className={`filter-chip ${tierFilter === "all" ? "active" : ""}`}
            onClick={() => setTierFilter("all")}
          >
            全部 ({data.sources.length})
          </button>
          <button
            className={`filter-chip ${tierFilter === "free" ? "active" : ""}`}
            onClick={() => setTierFilter("free")}
          >
            免费来源 ({data.sources.filter((s) => s.tier === "free").length})
          </button>
          <button
            className={`filter-chip ${tierFilter === "paid" ? "active" : ""}`}
            onClick={() => setTierFilter("paid")}
          >
            付费保底 ({data.sources.filter((s) => s.tier === "paid").length})
          </button>
        </div>
      </div>
      <div className="source-grid">
        {filtered.map((s) => {
          const isCircuitOpen = s.health?.circuitState === "open" || s.health?.anomaly === "circuit_open";
          const isHalfOpen = s.health?.circuitState === "half-open";

          return (
            <article className={`source-card ${isCircuitOpen ? "circuit-open" : ""}`} key={s.id}>
              <header>
                <div className="provider-icon">{s.preset.slice(0, 3).toUpperCase()}</div>
                <div>
                  <h3>{s.name}</h3>
                  <p>
                    {s.preset} · {s.protocol}
                  </p>
                </div>
                <span
                  className={`status-dot ${isCircuitOpen ? "danger" : isHalfOpen || !s.health ? "warn" : "ok"}`}
                  title={!s.health ? "尚无健康遥测" : isCircuitOpen ? "熔断中" : isHalfOpen ? "半开试探中" : "健康在线"}
                />
              </header>

              <div className="source-meta">
                <span>
                  <Globe2 size={13} />
                  端点地区: {s.endpoint_country}
                </span>
                <span>
                  <Database size={13} />
                  {s.geo_policy_id}
                </span>
                <Badge tone={s.tier === "free" ? "green" : "amber"}>
                  {s.tier === "free" ? "FREE" : "PAID GUARD"}
                </Badge>
              </div>

              {/* 评分分解 */}
              <div className="score-breakdown-box">
                <div className="score-breakdown-header">
                  <span>调度评分分量 (权重占比分解)</span>
                  <strong>
                    {s.health ? `成功率 ${(s.health.successRate * 100).toFixed(0)}% · 延迟 ${s.health.latencyEwmaMs}ms` : "尚无健康遥测，请先探测来源"}
                  </strong>
                </div>
                <div className="multi-segment-bar">
                  <div className="bar-segment bar-priority" style={{ width: "45%" }} title="人工优先级权重 45%" />
                  <div
                    className="bar-segment bar-success"
                    style={{ width: `${(s.health?.successRate ?? 0) * 30}%` }}
                    title="近期成功率权重 30%"
                  />
                  <div
                    className="bar-segment bar-quota"
                    style={{ width: `${(s.health?.remainingRatio ?? 0) * 15}%` }}
                    title="剩余额度权重 15%"
                  />
                  <div className="bar-segment bar-latency" style={{ width: "10%" }} title="延迟与负载权重 10%" />
                </div>
                <div className="score-legend">
                  <span>
                    <em style={{ background: "#7c8cff" }} />人工 45%
                  </span>
                  <span>
                    <em style={{ background: "#34d399" }} />成功率 30%
                  </span>
                  <span>
                    <em style={{ background: "#fbbf24" }} />额度 15%
                  </span>
                  <span>
                    <em style={{ background: "#c084fc" }} />延迟 10%
                  </span>
                </div>
              </div>

              <code>{s.base_url}</code>

              <button
                className="source-test"
                disabled={testingId === s.id}
                onClick={() => void testSource(s)}
              >
                <Beaker size={14} />
                {testingId === s.id ? "正在探测连通性..." : "探测凭据与模型目录连通性"}
              </button>
            </article>
          );
        })}
      </div>
    </section>
  );
}

// ========================================================
// 4. Routes Section (三级列表 + 决策模拟器)
// ========================================================
function RoutesSection({
  data,
  setData,
  notify,
  onAdd,
}: {
  data: AdminData;
  setData: Dispatch<SetStateAction<AdminData | null>>;
  notify: (msg: string) => void;
  onAdd: () => void;
}) {
  const [selectedModel, setSelectedModel] = useState(data.models[0]?.id ?? "deepseek-coder");
  const [simCountry, setSimCountry] = useState("JP");

  const modelRoutes = data.routes.filter((r) => r.logical_model_id === selectedModel);
  const freeRoutes = modelRoutes.filter((r) => r.tier === "free");
  const paidRoutes = modelRoutes.filter((r) => r.tier === "paid");
  const routeStatus = (route: RouteRow): { label: string; tone: "green" | "amber" | "neutral" } => {
    if (!route.health_updated_at) return { label: "未验证", tone: "amber" };
    if (route.circuit_open_until && route.circuit_open_until > Date.now()) return { label: "熔断", tone: "neutral" };
    if (route.circuit_open_until) return { label: route.half_open_probe_until && route.half_open_probe_until > Date.now() ? "半开探测中" : "等待半开探测", tone: "amber" };
    return { label: "健康", tone: "green" };
  };

  // Reorder routes
  const move = async (routeId: string, direction: -1 | 1) => {
    const list = [...modelRoutes].sort((a, b) => a.manual_priority - b.manual_priority);
    const idx = list.findIndex((r) => r.id === routeId);
    const target = idx + direction;
    if (idx < 0 || target < 0 || target >= list.length) return;
    [list[idx], list[target]] = [list[target]!, list[idx]!];
    const priorities = list.map((r, p) => ({ id: r.id, manualPriority: (p + 1) * 10 }));
    if (!data.demo) {
      try {
        await mutate("/routes/reorder", "PUT", { logicalModelId: selectedModel, priorities });
      } catch (error) {
        notify(describeError(error));
        return;
      }
    }
    setData((curr) =>
      curr
        ? {
            ...curr,
            routes: curr.routes.map((r) => {
              const item = priorities.find((p) => p.id === r.id);
              return item ? { ...r, manual_priority: item.manualPriority } : r;
            }),
          }
        : curr,
    );
    notify("人工优先级已更新 (最终尝试顺序仍受成功率与延迟综合影响)");
  };

  // Run Decision Simulator
  const simulation = useMemo(() => {
    if (simCountry === "UNKNOWN") {
      return {
        passed: [],
        excluded: modelRoutes.map((r) => ({
          route: r,
          reason: "请求国为 UNKNOWN，所有声明合规限制的供应商均被 Fail-Closed 拦截",
        })),
        decision: "无合法可用来源，网关将返回 451 geo_restricted (不发生跨模型回退)",
      };
    }
    const passed = modelRoutes.filter((r) => {
      const src = data.sources.find((s) => s.id === r.source_id);
      const policy = data.geoPolicies.find((p) => p.id === src?.geo_policy_id);
      if (!policy || !policy.restricted) return true;
      const allowed = JSON.parse(policy.allowed_request_countries_json) as string[];
      return allowed.includes(simCountry);
    });
    const excluded = modelRoutes
      .filter((r) => !passed.includes(r))
      .map((r) => ({
        route: r,
        reason: `供应商地域政策未将 ${simCountry} 列入允许列表 (合规规则不可被优先级覆盖)`,
      }));
    return {
      passed,
      excluded,
      decision:
        passed.length > 0
          ? `按 [免费层 -> 国家组 -> 综合打分] 将首选: ${passed[0]?.source_name}`
          : "全部来源被地域合规排除，返回 451 geo_restricted",
    };
  }, [simCountry, selectedModel, modelRoutes, data.sources, data.geoPolicies]);

  return (
    <>
      <section className="panel">
        <header className="section-header">
          <div>
            <span className="eyebrow">SCHEDULING PIPELINE & SIMULATOR</span>
            <h2>调度规则与决策模拟器</h2>
            <p>
              决策链路：供应商地域合规硬过滤 ➔ 免费/付费分层 ➔ 就近国家组排序 ➔ 综合加权评分。
            </p>
          </div>
          <button className="primary-button" onClick={onAdd}>
            <Plus size={16} />
            添加来源绑定
          </button>
        </header>

        {/* 决策模拟器 */}
        <div className="simulator-box">
          <div className="simulator-controls">
            <label>
              <strong>决策模拟推演：</strong>选择请求发起国代码
            </label>
            <select value={simCountry} onChange={(e) => setSimCountry(e.target.value)}>
              <option value="JP">JP - 日本</option>
              <option value="SG">SG - 新加坡</option>
              <option value="US">US - 美国</option>
              <option value="DE">DE - 德国</option>
              <option value="FR">FR - 法国</option>
              <option value="UNKNOWN">UNKNOWN - 未知请求国 (Fail-Closed)</option>
            </select>
            <span style={{ fontSize: "0.78rem", color: "var(--muted)" }}>
              当前测试逻辑模型: <code>{selectedModel}</code>
            </span>
          </div>

          <div className="decision-chain-funnel">
            <div className="funnel-step pass">
              <span className="funnel-step-tag">1. 合规硬过滤</span>
              <span className="funnel-step-desc">
                通过合规来源: {simulation.passed.length} 个；被拦截来源: {simulation.excluded.length} 个
              </span>
            </div>
            {simulation.excluded.map(({ route, reason }) => (
              <div className="funnel-step excluded" key={route.id}>
                <span className="funnel-step-tag" style={{ color: "var(--danger)" }}>
                  拦截排除
                </span>
                <span className="funnel-step-desc">
                  <code>{route.source_name}</code> - {reason}
                </span>
              </div>
            ))}
            <div className="funnel-step selected">
              <span className="funnel-step-tag" style={{ color: "var(--cyan)" }}>
                2. 调度执行结论
              </span>
              <span className="funnel-step-desc">
                <strong>{simulation.decision}</strong>
              </span>
            </div>
          </div>
        </div>

        {/* 模型切换与三级来源栈 */}
        <div className="filter-bar">
          <label style={{ fontSize: "0.82rem", fontWeight: 600, color: "var(--text)", marginRight: "10px" }}>
            查看逻辑模型:
          </label>
          <div className="filter-chips">
            {data.models.map((m) => (
              <button
                key={m.id}
                className={`filter-chip ${selectedModel === m.id ? "active" : ""}`}
                onClick={() => setSelectedModel(m.id)}
              >
                {m.display_name} ({m.id})
              </button>
            ))}
          </div>
        </div>

        <div className="route-stack">
          {/* 免费梯队 */}
          {freeRoutes.length > 0 && (
            <div className="route-tier-group">
              <div className="tier-divider free">
                <span>优先调度梯队 · FREE TIER (100% 优先尝试)</span>
                <div className="tier-divider-line" />
              </div>
              {freeRoutes.map((r, i) => (
                <div className="route-line draggable" key={r.id}>
                  <span className="route-order">
                    <button disabled={i === 0} onClick={() => void move(r.id, -1)} aria-label="提高人工优先级">
                      <ArrowUp size={13} />
                    </button>
                    <button disabled={i === freeRoutes.length - 1} onClick={() => void move(r.id, 1)} aria-label="降低人工优先级">
                      <ArrowDown size={13} />
                    </button>
                  </span>
                  <span className="route-index">{i + 1}</span>
                  <div>
                    <strong>{r.source_name}</strong>
                    <small>
                      上游实际 Model: <code>{r.upstream_model_id}</code> · 综合健康评分:{" "}
                      <strong>{r.scoreBreakdown?.totalScore.toFixed(1) ?? "85.0"}</strong>
                    </small>
                  </div>
                  <Badge tone={routeStatus(r).tone}>{routeStatus(r).label}</Badge>
                  <Badge tone="green">FREE</Badge>
                  <Badge tone="blue">{r.endpoint_country}</Badge>
                  <span className="score">P{r.manual_priority}</span>
                </div>
              ))}
            </div>
          )}

          {/* 付费梯队 */}
          {paidRoutes.length > 0 && (
            <div className="route-tier-group" style={{ marginTop: "16px" }}>
              <div className="tier-divider paid">
                <span>后备保底梯队 · PAID FALLBACK (仅免费源耗尽且在预算内时触发)</span>
                <div className="tier-divider-line" />
              </div>
              {paidRoutes.map((r, i) => (
                <div className="route-line draggable" key={r.id}>
                  <span className="route-order">
                    <button disabled={i === 0} onClick={() => void move(r.id, -1)} aria-label="提高人工优先级">
                      <ArrowUp size={13} />
                    </button>
                    <button disabled={i === paidRoutes.length - 1} onClick={() => void move(r.id, 1)} aria-label="降低人工优先级">
                      <ArrowDown size={13} />
                    </button>
                  </span>
                  <span className="route-index">{freeRoutes.length + i + 1}</span>
                  <div>
                    <strong>{r.source_name}</strong>
                    <small>
                      上游实际 Model: <code>{r.upstream_model_id}</code> · 受全局/单源预算双硬上限保护
                    </small>
                  </div>
                  <Badge tone={routeStatus(r).tone}>{routeStatus(r).label}</Badge>
                  <Badge tone="amber">PAID</Badge>
                  <Badge tone="blue">{r.endpoint_country}</Badge>
                  <span className="score">P{r.manual_priority}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

// ========================================================
// 5. Countries Section (国家策略管理)
// ========================================================
function CountriesSection({
  data,
  setData,
  notify,
  onEdit,
}: {
  data: AdminData;
  setData: Dispatch<SetStateAction<AdminData | null>>;
  notify: (msg: string) => void;
  onEdit: (id: string) => void;
}) {
  const [groupsText, setGroupsText] = useState(() =>
    (JSON.parse(data.priority.groups_json) as string[][]).map((g) => g.join(", ")).join("\n"),
  );

  const savePriority = async () => {
    const parsed = groupsText
      .split("\n")
      .map((line) =>
        line
          .split(",")
          .map((item) => item.trim().toUpperCase())
          .filter(Boolean),
      )
      .filter((g) => g.length);
    if (!data.demo) {
      try {
        await mutate("/country-priorities/global", "PUT", { name: "Global default", groups: parsed, allowUnknown: false });
      } catch (error) {
        notify(describeError(error));
        return;
      }
    }
    setData((curr) =>
      curr ? { ...curr, priority: { ...curr.priority, groups_json: JSON.stringify(parsed) } } : curr,
    );
    notify("国家调度优先级序列已保存");
  };

  return (
    <div className="country-layout">
      <section className="panel">
        <header className="section-header">
          <div>
            <span className="eyebrow">ORDER OF PREFERENCE</span>
            <h2>端点国家组排序</h2>
            <p>每行一组同等优先级，行越靠上优先级越高。合规前提下的就近优选。</p>
          </div>
        </header>
        <div style={{ padding: "16px 20px" }}>
          <textarea
            value={groupsText}
            onChange={(e) => setGroupsText(e.target.value)}
            rows={7}
            spellCheck={false}
          />
          <div style={{ marginTop: "12px", display: "grid", gap: "6px", fontSize: "0.76rem", color: "var(--muted)" }}>
            <p style={{ margin: 0 }}>
              <Info size={13} style={{ verticalAlign: "middle", marginRight: 4 }} />
              <strong>两类兜底差异说明：</strong>
            </p>
            <p style={{ margin: 0 }}>
              1. <strong>未列出的已知国家</strong>：会自动排到序列末尾，但<strong>仍然参与调度</strong>。
            </p>
            <p style={{ margin: 0 }}>
              2. <strong>端点标记为 UNKNOWN 的来源</strong>：默认<strong>整体不参与调度</strong>（需显式批准）。
            </p>
          </div>
        </div>
        <div className="form-actions" style={{ padding: "0 20px 20px" }}>
          <button className="primary-button" onClick={() => void savePriority()}>
            <Save size={15} />
            保存国家组优先级
          </button>
        </div>
      </section>

      <section className="panel">
        <header className="section-header">
          <div>
            <span className="eyebrow">FAIL-CLOSED COMPLIANCE</span>
            <h2>供应商国家策略与核验周期</h2>
            <p>合规信息前置：默认 30 天核验周期，超期策略自动失败关闭拦截。</p>
          </div>
        </header>
        <div className="policy-list">
          {data.geoPolicies.map((p) => {
            const countries = JSON.parse(p.allowed_request_countries_json) as string[];
            const staleDays = Math.floor((Date.now() - p.verified_at) / 86400000);
            const remainingDays = Math.max(0, p.valid_days - staleDays);
            const percent = Math.min(100, Math.max(0, (remainingDays / p.valid_days) * 100));

            return (
              <article key={p.id}>
                <header>
                  <div>
                    <h3>{p.name}</h3>
                    <p>{p.restricted ? `${countries.length} 个允许请求国家` : "未声明地域限制"}</p>
                  </div>
                  <div className="policy-actions">
                    <Badge tone={remainingDays <= 0 ? "neutral" : remainingDays <= 7 ? "amber" : "green"}>
                      {remainingDays <= 0 ? "已超期阻断" : `核验剩余 ${remainingDays} 天`}
                    </Badge>
                    <button className="text-button" onClick={() => onEdit(p.id)}>
                      编辑并核验
                    </button>
                  </div>
                </header>

                {/* 倒计时进度条 */}
                <div className="countdown-container">
                  <div className="countdown-bar-wrap">
                    <div
                      className={`countdown-bar-fill ${
                        remainingDays <= 0 ? "expired" : remainingDays <= 7 ? "stale-warning" : "healthy"
                      }`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>

                <div className="country-chips">
                  {countries.slice(0, 10).map((c) => (
                    <span key={c}>{c}</span>
                  ))}
                  {countries.length > 10 && <span>+{countries.length - 10}</span>}
                </div>

                <div className="audit-meta-line">
                  <Clock size={12} />
                  <span>上次核验者: {p.last_verified_by || "admin"} · {timeAgo(p.verified_at)}</span>
                  {p.policy_url && (
                    <a href={p.policy_url} target="_blank" rel="noreferrer" style={{ marginLeft: "auto" }}>
                      <Link2 size={13} />
                      查看官方支持国家列表
                    </a>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// ========================================================
// 6. Opportunities Section (试用候选箱)
// ========================================================
function OpportunitiesSection({
  data,
  setData,
  notify,
  onRequestFriction,
}: {
  data: AdminData;
  setData: Dispatch<SetStateAction<AdminData | null>>;
  notify: (msg: string) => void;
  onRequestFriction: (title: string, desc: string, action: () => Promise<void>) => void;
}) {
  const [tab, setTab] = useState<"pending" | "approved" | "dismissed">("pending");
  const filtered = data.opportunities.filter((o) => o.status === tab);

  const approve = (item: OpportunityRow) => {
    onRequestFriction(
      `批准官方试用机会: ${item.title}`,
      "请确认已审核供应商条款。批准仅更新候选审核状态；仍需添加来源凭据并绑定逻辑模型后才能参与调度。",
      async () => {
        if (!data.demo) {
          try {
            await mutate(`/opportunities/${item.id}`, "PUT", { status: "approved" });
          } catch (error) {
            notify(describeError(error));
            return;
          }
        }
        setData((curr) =>
          curr
            ? {
                ...curr,
                opportunities: curr.opportunities.map((o) =>
                  o.id === item.id ? { ...o, status: "approved" } : o,
                ),
              }
            : curr,
        );
        notify("候选已批准；请添加来源凭据并绑定模型后接入路由");
      },
    );
  };

  const recall = async (item: OpportunityRow) => {
    if (!data.demo) {
      try {
        await mutate(`/opportunities/${item.id}`, "PUT", { status: "pending" });
      } catch (error) {
        notify(describeError(error));
        return;
      }
    }
    setData((curr) =>
      curr
        ? {
            ...curr,
            opportunities: curr.opportunities.map((o) =>
              o.id === item.id ? { ...o, status: "pending" } : o,
            ),
          }
        : curr,
    );
    notify("已撤回至待审核候选箱");
  };

  const dismiss = async (item: OpportunityRow) => {
    if (!data.demo) {
      try {
        await mutate(`/opportunities/${item.id}`, "PUT", { status: "dismissed" });
      } catch (error) {
        notify(describeError(error));
        return;
      }
    }
    setData((curr) =>
      curr
        ? {
            ...curr,
            opportunities: curr.opportunities.map((o) =>
              o.id === item.id ? { ...o, status: "dismissed" } : o,
            ),
          }
        : curr,
    );
    notify("已忽略此候选机会");
  };

  return (
    <section className="panel">
      <header className="section-header">
        <div>
          <span className="eyebrow">HUMAN-IN-THE-LOOP DISCOVERY</span>
          <h2>官方试用机会候选箱</h2>
          <p>定时扫描官方页面发布。严禁静默启用凭据，所有机会必须由管理员显式审查条款后接入。</p>
        </div>
      </header>
      <div className="filter-bar">
        <div className="filter-chips">
          <button className={`filter-chip ${tab === "pending" ? "active" : ""}`} onClick={() => setTab("pending")}>
            待人工核验 ({data.opportunities.filter((o) => o.status === "pending").length})
          </button>
          <button className={`filter-chip ${tab === "approved" ? "active" : ""}`} onClick={() => setTab("approved")}>
            已批准生效 ({data.opportunities.filter((o) => o.status === "approved").length})
          </button>
          <button className={`filter-chip ${tab === "dismissed" ? "active" : ""}`} onClick={() => setTab("dismissed")}>
            已忽略归档 ({data.opportunities.filter((o) => o.status === "dismissed").length})
          </button>
        </div>
      </div>
      <div className="opportunity-list">
        {filtered.map((item) => (
          <article key={item.id}>
            <div className="provider-icon">
              <Sparkles size={18} />
            </div>
            <div>
              <div className="badge-row">
                <Badge tone="blue">{item.provider}</Badge>
                <Badge>{item.opportunity_type}</Badge>
                <Badge tone={item.status === "pending" ? "amber" : item.status === "approved" ? "green" : "neutral"}>
                  {item.status.toUpperCase()}
                </Badge>
                {item.probeResult && (
                  <Badge tone={item.probeResult.reachable ? "green" : "amber"}>
                    探测: {item.probeResult.reachable ? "端点可达" : "不可达"} · {item.probeResult.latencyMs}ms
                  </Badge>
                )}
              </div>
              <h3>{item.title}</h3>
              <p>{item.description}</p>
              <div style={{ display: "flex", gap: "16px", alignItems: "center" }}>
                <a href={item.url} target="_blank" rel="noreferrer">
                  查看官方发布页面 <ExternalLink size={13} />
                </a>
                <span style={{ fontSize: "0.75rem", color: "var(--muted)" }}>
                  发现于: {timeAgo(item.discovered_at)}
                </span>
              </div>
            </div>
            <div className="opportunity-actions">
              {item.status === "pending" && (
                <>
                  <button className="secondary-button" onClick={() => void dismiss(item)}>
                    忽略
                  </button>
                  <button className="primary-button" onClick={() => approve(item)}>
                    批准候选
                  </button>
                </>
              )}
              {item.status !== "pending" && (
                <button className="secondary-button" onClick={() => void recall(item)}>
                  <Undo2 size={14} />
                  撤回至候选箱
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

// ========================================================
// 7. Keys Section (Gateway Keys 凭据只写不读)
// ========================================================
function KeysSection({
  data,
  setData,
  notify,
  onAdd,
  onRequestFriction,
}: {
  data: AdminData;
  setData: Dispatch<SetStateAction<AdminData | null>>;
  notify: (msg: string) => void;
  onAdd: () => void;
  onRequestFriction: (title: string, desc: string, action: () => Promise<void>) => void;
}) {
  const [search, setSearch] = useState("");
  const filtered = data.keys.filter(
    (k) => k.name.toLowerCase().includes(search.toLowerCase()) || k.key_prefix.includes(search),
  );

  const revoke = (id: string, name: string) => {
    onRequestFriction(
      `吊销 Gateway Key: ${name}`,
      "吊销后该凭据将立即失效，正在使用它的客户端调用将收到 401 invalid_api_key 错误，此操作不可逆！",
      async () => {
        if (!data.demo) {
          try {
            await mutate(`/keys/${id}`, "DELETE");
          } catch (error) {
            notify(describeError(error));
            return;
          }
        }
        setData((curr) =>
          curr ? { ...curr, keys: curr.keys.map((k) => (k.id === id ? { ...k, enabled: 0 } : k)) } : curr,
        );
        notify("Gateway Key 已吊销生效");
      },
    );
  };

  return (
    <section className="panel">
      <header className="section-header">
        <div>
          <span className="eyebrow">CLIENT AUTHORIZATION</span>
          <h2>Gateway Keys 凭据管理</h2>
          <p>凭据只写不读：创建后仅一次性可见，数据库只存加盐 HMAC-SHA256 摘要，列表永久掩码。</p>
        </div>
        <button className="primary-button" onClick={onAdd}>
          <Plus size={16} />
          签发 Gateway Key
        </button>
      </header>
      <div className="filter-bar">
        <div className="search-input-wrap">
          <Search size={16} />
          <input placeholder="搜索 Key 标识或前缀..." value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>名称标识</th>
              <th>Key 脱敏掩码</th>
              <th>速率与并发租约</th>
              <th>最后调用</th>
              <th>状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((key) => (
              <tr key={key.id}>
                <td>
                  <strong>{key.name}</strong>
                </td>
                <td>
                  <code>ftr_live_{key.key_prefix}••••••••</code>
                </td>
                <td>
                  {key.requests_per_minute} RPM · {key.max_concurrency} 并发
                </td>
                <td>{timeAgo(key.last_used_at)}</td>
                <td>
                  <Badge tone={key.enabled ? "green" : "neutral"}>{key.enabled ? "有效可用" : "已吊销"}</Badge>
                </td>
                <td>
                  {key.enabled ? (
                    <button className="danger-button" onClick={() => revoke(key.id, key.name)}>
                      <Ban size={14} />
                      吊销
                    </button>
                  ) : (
                    <span style={{ color: "var(--muted)" }}>—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ========================================================
// 8. Logs Section (请求日志 / 决策解释器)
// ========================================================
function LogsSection({ data }: { data: AdminData }) {
  const [selectedLogId, setSelectedLogId] = useState<string | null>(data.recentLogs[0]?.id ?? null);
  const selectedLog = data.recentLogs.find((l) => l.id === selectedLogId);

  return (
    <section className="panel">
      <header className="section-header">
        <div>
          <span className="eyebrow">REQUEST DECISION EXPLAINER</span>
          <h2>请求日志与决策解释器</h2>
          <p>
            {data.demo ? "以下为演示请求决策链。" : "当前尚未接入请求决策日志查询，请在 Cloudflare Workers 日志中检查实际请求。"}
          </p>
        </div>
      </header>

      {/* 零 Prompt 隐私声明 */}
      <div style={{ padding: "0 20px" }}>
        <div className="privacy-banner">
          <EyeOff size={16} />
          <span>
            <strong>零内容存储承诺</strong>：本网关不记录任何提示词、代码或模型补全正文。日志仅记录端点、延迟、状态码与决策拓扑。
          </span>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.2fr 1fr", gap: "16px", padding: "0 20px 20px" }}>
        {/* 日志列表 */}
        <div className="table-wrap table-wrap--scroll" style={{ padding: 0 }}>
          <table>
            <thead>
              <tr>
                <th>时间</th>
                <th>模型 / 来源</th>
                <th>发起国</th>
                <th>状态</th>
                <th>耗时</th>
              </tr>
            </thead>
            <tbody>
              {data.recentLogs.map((log) => (
                <tr
                  key={log.id}
                  style={{
                    cursor: "pointer",
                    background: selectedLogId === log.id ? "rgba(124, 140, 255, 0.1)" : undefined,
                  }}
                  onClick={() => setSelectedLogId(log.id)}
                >
                  <td>
                    <small>{timeAgo(log.timestamp)}</small>
                  </td>
                  <td>
                    <strong>{log.logicalModel}</strong>
                    <small>{log.finalSource || "无可用上游"}</small>
                  </td>
                  <td>
                    <Badge tone="blue">{log.requestCountry}</Badge>
                  </td>
                  <td>
                    <Badge tone={log.status === 200 ? "green" : log.status === 451 ? "amber" : "neutral"}>
                      HTTP {log.status}
                    </Badge>
                  </td>
                  <td>{log.latencyMs}ms</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* 决策解释抽屉 */}
        {selectedLog ? (
          <div
            style={{
              padding: "16px",
              background: "rgba(0,0,0,0.3)",
              border: "1px solid var(--line)",
              borderRadius: "var(--radius-md)",
            }}
          >
            <h3 style={{ margin: "0 0 10px", fontSize: "0.95rem" }}>
              决策时间线追溯 · 请求 ID: <code>{selectedLog.id}</code>
            </h3>

            {selectedLog.errorExplanation && (
              <div
                style={{
                  padding: "10px 12px",
                  borderRadius: "var(--radius-sm)",
                  background: selectedLog.switchableError ? "var(--brand-a10)" : "var(--amber-subtle)",
                  border: "1px solid var(--line)",
                  fontSize: "0.78rem",
                  marginBottom: "12px",
                }}
              >
                <strong style={{ display: "block", marginBottom: 2 }}>
                  {selectedLog.switchableError ? "容灾切源机制激活" : "终止拦截说明"}
                </strong>
                {selectedLog.errorExplanation}
              </div>
            )}

            <div className="log-timeline">
              {selectedLog.decisionChain.map((step, idx) => (
                <div className="timeline-item" key={idx}>
                  <div className="timeline-marker">
                    {step.status === "pass" && <Check size={12} color="var(--lime)" />}
                    {step.status === "excluded" && <Ban size={12} color="var(--danger)" />}
                    {step.status === "selected" && <Route size={12} color="var(--cyan)" />}
                    {step.status === "failed" && <AlertTriangle size={12} color="var(--amber)" />}
                  </div>
                  <div className="timeline-content">
                    <strong>{step.stage}</strong>
                    <p>{step.detail}</p>
                  </div>
                </div>
              ))}
            </div>

            {selectedLog.status === 503 && (
              <p style={{ fontSize: "0.72rem", color: "var(--muted)", marginTop: 10 }}>
                * 503 all_sources_failed 说明：同模型全部候选来源均已尝试失败，网关未跨模型回退（此为系统严格安全规范）。
              </p>
            )}
          </div>
        ) : (
          <div style={{ padding: "20px", color: "var(--muted)" }}>点击左侧请求查看完整决策链路</div>
        )}
      </div>
    </section>
  );
}

// ========================================================
// 9. Budget Section (预算与账本：已结算 vs 预留中双态)
// ========================================================
function BudgetSection({
  data,
  setData,
  notify,
  onRequestFriction,
}: {
  data: AdminData;
  setData: Dispatch<SetStateAction<AdminData | null>>;
  notify: (msg: string) => void;
  onRequestFriction: (title: string, desc: string, action: () => Promise<void>) => void;
}) {
  const [budgetInput, setBudgetInput] = useState(data.budget.globalLimitUsd);

  const totalLimit = data.budget.globalLimitUsd;
  const settledPercent = totalLimit > 0 ? Math.min(100, (data.budget.settledUsd / totalLimit) * 100) : 0;
  const reservedPercent = totalLimit > 0 ? Math.min(100 - settledPercent, (data.budget.reservedUsd / totalLimit) * 100) : 0;

  const saveBudget = () => {
    onRequestFriction(
      `修改全局付费预算硬上限至 $${budgetInput.toFixed(2)}`,
      "付费保底来源仅在免费来源全部耗尽后激活。设为 0 将彻底关闭付费保底；调高金额可能导致上游账单产生，请确认预算限额是否经过团队核准。",
      async () => {
        if (!data.demo) {
          try {
            await mutate("/settings/global-paid-budget", "PUT", {
              amountLimitUsd: budgetInput,
              resetPeriod: "monthly",
              resetTimezone: "Asia/Shanghai",
            });
          } catch (error) {
            notify(describeError(error));
            return;
          }
        }
        setData((curr) =>
          curr
            ? {
                ...curr,
                budget: { ...curr.budget, globalLimitUsd: budgetInput, remainingUsd: Math.max(0, budgetInput - curr.budget.settledUsd - curr.budget.reservedUsd) },
                dashboard: { ...curr.dashboard, globalPaidBudgetUsd: budgetInput },
              }
            : curr,
        );
        notify("全局付费预算已调整生效");
      },
    );
  };

  return (
    <div className="settings-grid">
      <section className="panel">
        <header className="section-header">
          <div>
            <span className="eyebrow">GUARDRAILS & LEDGER</span>
            <h2>全局预算账本 (双态区分)</h2>
            <p>已结算额度实心显示，流式请求并发预留中额度斜纹显示，避免误判真实可用预算。</p>
          </div>
        </header>

        <div style={{ padding: "20px 24px" }}>
          {/* 双态条形图 */}
          <div className="budget-dual-bar">
            <div className="budget-bar-track">
              <div className="budget-bar-settled" style={{ width: `${settledPercent}%` }} title="已结算消耗" />
              <div className="budget-bar-reserved" style={{ width: `${reservedPercent}%` }} title="流式并发预留中" />
            </div>
            <div className="budget-legend-row">
              <div className="budget-legend-item">
                <em style={{ background: "linear-gradient(90deg, #4c57c4, #7c8cff)" }} />
                <span>
                  已结算: <strong>${data.budget.settledUsd.toFixed(2)}</strong>
                </span>
              </div>
              <div className="budget-legend-item">
                <em
                  style={{
                    background: "repeating-linear-gradient(45deg, #fbbf24, #fbbf24 4px, #b45309 4px, #b45309 8px)",
                  }}
                />
                <span>
                  预留中 (In-Flight): <strong>${data.budget.reservedUsd.toFixed(2)}</strong>
                </span>
              </div>
              <div className="budget-legend-item" style={{ marginLeft: "auto" }}>
                <span>
                  剩余可用: <strong>${data.budget.remainingUsd.toFixed(2)}</strong> / 限额 ${totalLimit.toFixed(2)}
                </span>
              </div>
            </div>
          </div>

          <label className="stack-label" style={{ padding: "16px 0 0" }}>
            调整每月金额上限 (USD)
            <div className="money-input">
              <span>$</span>
              <input
                type="number"
                min="0"
                step="0.5"
                value={budgetInput}
                onChange={(e) => setBudgetInput(Number(e.target.value))}
              />
            </div>
            <small>设为 0 将完全关闭付费调度。调整需显式确认。</small>
          </label>

          <div style={{ marginTop: "16px" }}>
            <button className="primary-button" onClick={saveBudget}>
              <Save size={15} />
              提交调整 (触发确认)
            </button>
          </div>
        </div>
      </section>

      <section className="panel safeguard">
        <BadgeDollarSign size={32} />
        <h2>预留制工作原理</h2>
        <p>
          在请求发起前，调度状态层按照估算输入和模型最大输出预留资金；上游返回 usage 后多退少补结算；流式无
          usage 时保留预留值。
        </p>
        <div>
          <Activity size={16} />
          到达硬上限秒级拦截，绝无意外账单
        </div>
      </section>
    </div>
  );
}

// ========================================================
// 10. OpenCode Config Section (客户端配置生成器)
// ========================================================
function ConfigSection({ data, notify }: { data: AdminData; notify: (msg: string) => void }) {
  const [gatewayBaseUrl, setGatewayBaseUrl] = useState("https://js.55329710.xyz/v1");
  const [protocol, setProtocol] = useState<"chat" | "responses">("chat");
  const [selectedModel, setSelectedModel] = useState(data.models[0]?.id ?? "deepseek-coder");
  const [configCopied, copyConfig] = useCopyButton();

  const modelObj = data.models.find((m) => m.id === selectedModel);
  const healthySources = data.sources.filter((s) => s.enabled && s.health?.circuitState !== "open");
  const hasHealthy = healthySources.length > 0;

  const configJson = useMemo(() => {
    return JSON.stringify(
      {
        $schema: "https://opencode.ai/config.json",
        provider: {
          "free-token-router": {
            npm: protocol === "chat" ? "@ai-sdk/openai-compatible" : "@ai-sdk/openai",
            name: "FreeTokenRouter",
            options: {
              baseURL: gatewayBaseUrl.trim() || "https://js.55329710.xyz/v1",
              apiKey: "{env:FREE_TOKEN_ROUTER_KEY}",
            },
            models: Object.fromEntries(
              data.models
                .filter((m) => m.enabled)
                .map((m) => [
                  m.id,
                  {
                    name: `${m.display_name} via FTR`,
                    limit: { context: m.context_window, output: m.max_output_tokens },
                  },
                ]),
            ),
          },
        },
      },
      null,
      2,
    );
  }, [protocol, data.models, gatewayBaseUrl]);

  return (
    <section className="panel">
      <header className="section-header">
        <div>
          <span className="eyebrow">CLIENT CONFIGURATION</span>
          <h2>OpenCode / Cursor 配置生成器</h2>
          <p>
            客户端只需持有 Gateway Key，请求统一发往网关。支持 Chat Completions 与 Responses 协议切换。
          </p>
        </div>
      </header>

      <div style={{ padding: "20px 24px" }}>
        <label className="stack-label">Router 网关 Base URL
          <input type="url" value={gatewayBaseUrl} onChange={(e) => setGatewayBaseUrl(e.target.value)} placeholder="https://js.55329710.xyz/v1" />
          <small>填写 Router Worker 的实际地址，包含 /v1。</small>
        </label>
        {/* 协议切换 */}
        <div className="protocol-switch-bar">
          <button
            className={`protocol-btn ${protocol === "chat" ? "active" : ""}`}
            onClick={() => setProtocol("chat")}
          >
            Chat Completions (@ai-sdk/openai-compatible)
          </button>
          <button
            className={`protocol-btn ${protocol === "responses" ? "active" : ""}`}
            onClick={() => setProtocol("responses")}
          >
            Responses API (@ai-sdk/openai)
          </button>
        </div>

        {/* 无健康源提醒 */}
        {!hasHealthy && (
          <div className="no-health-warning">
            <AlertTriangle size={18} />
            <span>当前所有来源均处于熔断或已停用状态，生成的配置将暂时不可用，请先排查上游端点！</span>
          </div>
        )}

        <div className="config-result">
          <pre>{configJson}</pre>
          <button
            className="secondary-button"
            onClick={async () => {
              const ok = await copyConfig(configJson);
              notify(ok ? "配置 JSON 已复制至剪贴板" : "复制失败，请手动选择并复制配置 JSON");
            }}
          >
            {configCopied ? <Check size={14} /> : <Clipboard size={14} />}
            {configCopied ? "已复制" : "复制配置"}
          </button>
        </div>

        <p style={{ fontSize: "0.78rem", color: "var(--muted)", marginTop: "12px" }}>
          * 安全提示：请将真实 Key 放置于环境变量 <code>FREE_TOKEN_ROUTER_KEY</code>，切勿直接提交明文至 Git 仓库。
        </p>
      </div>
    </section>
  );
}

// ========================================================
// 11. Playground Section (网关实时调试台)
// ========================================================
function PlaygroundSection({
  data,
  notify,
}: {
  data: AdminData;
  notify: (msg: string, tone?: "success" | "warn" | "error") => void;
}) {
  const [model, setModel] = useState(data.models[0]?.id ?? "deepseek-coder");
  const [gatewayKey, setGatewayKey] = useState("");
  const [prompt, setPrompt] = useState("用 TypeScript 写一个斐波那契数列函数，并包含类型推断。");
  const [running, setRunning] = useState(false);
  const [output, setOutput] = useState("");
  const [meta, setMeta] = useState<{ status: number; latency: number; source?: string } | null>(null);

  const runTest = async () => {
    setRunning(true);
    setOutput("");
    setMeta(null);
    const start = Date.now();
    try {
      if (data.demo) {
        await new Promise((r) => setTimeout(r, 680));
        const matched = data.routes.find((r) => r.logical_model_id === model);
        setOutput(
          `// 由 FreeTokenRouter 模拟响应 (已路由至: ${matched?.source_name ?? "首选上游"})\n\nfunction fibonacci(n: number): number {\n  if (n <= 1) return n;\n  let prev = 0, curr = 1;\n  for (let i = 2; i <= n; i++) {\n    const next = prev + curr;\n    prev = curr;\n    curr = next;\n  }\n  return curr;\n}\n\n// 测试输出:\nconsole.log(fibonacci(10)); // 55`,
        );
        setMeta({ status: 200, latency: Date.now() - start, source: matched?.source_name ?? "Demo Upstream" });
        notify("网关调度测试调用成功", "success");
      } else {
        const res = await fetch("/api/playground", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            model,
            prompt,
            gatewayKey,
          }),
          signal: AbortSignal.timeout(125_000),
        });
        const elapsed = Date.now() - start;
        const json = (await res.json()) as { choices?: Array<{ message?: { content?: string } }>; error?: string | { message?: string } };
        if (!res.ok) {
          setMeta({ status: res.status, latency: elapsed });
          throw new Error(typeof json.error === "string" ? json.error : json.error?.message ?? `HTTP ${res.status}`);
        }
        setOutput(json.choices?.[0]?.message?.content ?? JSON.stringify(json, null, 2));
        setMeta({ status: res.status, latency: elapsed, source: "Cloudflare Edge Live" });
        notify(`网关测试成功 (${elapsed} ms)`, "success");
      }
    } catch (err) {
      setOutput(`调用失败: ${err instanceof Error ? err.message : String(err)}`);
      setMeta((current) => current ?? { status: 500, latency: Date.now() - start });
      notify("测试请求未成功", "error");
    } finally {
      setRunning(false);
    }
  };

  return (
    <section className="panel">
      <header className="section-header">
        <div>
          <span className="eyebrow">INTERACTIVE DEBUGGING</span>
          <h2>网关实时调试台</h2>
          <p>无需外部工具，在管理控制台内直接向网关发送 Prompt，检验同模型路由流转与响应延迟。</p>
        </div>
      </header>
      <div className="playground-layout">
        <div className="playground-input-panel">
          {!data.demo && <label>Gateway Key（仅本次页面内使用）
            <input type="password" autoComplete="off" value={gatewayKey} onChange={(e) => setGatewayKey(e.target.value)} />
          </label>}
          <label>
            测试目标逻辑模型
            <select value={model} onChange={(e) => setModel(e.target.value)}>
              {data.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.display_name} ({m.id})
                </option>
              ))}
            </select>
          </label>
          <label>
            测试输入 Prompt
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} rows={7} />
          </label>
          <button className="primary-button" disabled={running || !data.models.length || !prompt.trim() || (!data.demo && !gatewayKey.trim())} onClick={() => void runTest()}>
            <Play size={16} />
            {running ? "正在调度边缘上游..." : "发送测试请求"}
          </button>
        </div>

        <div className="playground-output-panel">
          <div className="playground-meta-bar">
            {meta ? (
              <>
                <Badge tone={meta.status === 200 ? "green" : "amber"}>HTTP {meta.status}</Badge>
                <Badge tone="blue">首字节延迟: {meta.latency} ms</Badge>
                {meta.source && <Badge tone="neutral">命中端点: {meta.source}</Badge>}
              </>
            ) : (
              <span style={{ color: "var(--muted)", fontSize: "0.78rem" }}>等待发送测试请求...</span>
            )}
          </div>
          <div className="playground-result-box">
            {output || <span style={{ color: "var(--muted)" }}>点击左侧“发送测试请求”查看实际模型返回</span>}
          </div>
        </div>
      </div>
    </section>
  );
}

// ========================================================
// 12. Audits Section (审计记录)
// ========================================================
function AuditsSection({ data }: { data: AdminData }) {
  return (
    <section className="panel">
      <header className="section-header">
        <div>
          <span className="eyebrow">AUDIT LOG</span>
          <h2>安全与合规审计日志</h2>
          <p>记录关键配置修改者与事件。凭据明文永不写入审计事件。</p>
        </div>
      </header>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>时间戳</th>
              <th>操作者 (Actor)</th>
              <th>动作</th>
              <th>影响对象</th>
              <th>操作摘要</th>
            </tr>
          </thead>
          <tbody>
            {data.audits.map((item) => (
              <tr key={item.id}>
                <td>{new Date(item.created_at).toLocaleString("zh-CN")}</td>
                <td>{item.actor}</td>
                <td>
                  <Badge tone="blue">{item.action.toUpperCase()}</Badge>
                </td>
                <td>
                  <strong>{item.entity_type}</strong>
                  <small>{item.entity_id}</small>
                </td>
                <td>
                  <code>{item.summary_json}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ========================================================
// Helper Forms: SourceDynamicForm (预设感知动态表单)
// ========================================================
function SourceDynamicForm({
  data,
  onCancel,
  onSubmit,
}: {
  data: AdminData;
  onCancel: () => void;
  onSubmit: (data: Record<string, unknown>) => Promise<void> | void;
}) {
  const [preset, setPreset] = useState("nvidia");
  const [baseUrl, setBaseUrl] = useState("https://integrate.api.nvidia.com/v1");
  const [tier, setTier] = useState<"free" | "paid">("free");
  const [urlError, setUrlError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [credential, setCredential] = useState("");
  const [geoPolicyId, setGeoPolicyId] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Model binding states
  const [attachModel, setAttachModel] = useState(true);
  const [targetModelId, setTargetModelId] = useState(() => data.models[0]?.id || "__new__");
  const [isNewModel, setIsNewModel] = useState(() => data.models.length === 0);
  const [newModelId, setNewModelId] = useState("deepseek-v4-flash");
  const [newModelDisplayName, setNewModelDisplayName] = useState("DeepSeek V4 Flash");
  const [upstreamModelId, setUpstreamModelId] = useState("deepseek-ai/deepseek-v4-flash");

  // Default URL & upstream model on preset change
  const handlePresetChange = (val: string) => {
    setPreset(val);
    setGeoPolicyId("");
    const nextUrl =
      val === "nvidia"
        ? "https://integrate.api.nvidia.com/v1"
        : val === "google"
          ? "https://generativelanguage.googleapis.com/v1beta/openai"
          : val === "openrouter"
            ? "https://openrouter.ai/api/v1"
            : val === "deepseek"
              ? "https://api.deepseek.com/v1"
              : val === "moonshot"
                ? "https://api.moonshot.cn/v1"
                : "https://my-domain.example.com/v1";
    setBaseUrl(nextUrl);
    if (val === "custom") validateUrl(nextUrl);
    else setUrlError(null);

    const suggestions = getPresetModelSuggestions(val);
    const first = suggestions[0];
    if (first) {
      setUpstreamModelId(first);
    }
  };

  // Instant SSRF validation for Custom
  const validateUrl = (urlStr: string) => {
    try {
      const u = new URL(urlStr);
      if (u.protocol !== "https:") {
        setUrlError("安全红线：仅允许 HTTPS 端点");
        return false;
      }
      const host = u.hostname.toLowerCase();
      if (
        host === "localhost" ||
        host === "127.0.0.1" ||
        host.startsWith("10.") ||
        host.startsWith("192.168.") ||
        host.startsWith("172.16.") ||
        host.startsWith("169.254.")
      ) {
        setUrlError("SSRF 防护：严禁指向 localhost、私网或链路本地地址");
        return false;
      }
      setUrlError(null);
      return true;
    } catch {
      setUrlError("请输入合法的标准 URL");
      return false;
    }
  };

  const missingRequired =
    !name.trim() ||
    !credential.trim() ||
    !geoPolicyId ||
    (attachModel && (!upstreamModelId.trim() || (isNewModel && !newModelId.trim())));

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (missingRequired || isSubmitting) return;
    if (preset === "custom" && !validateUrl(baseUrl)) return;
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const form = new FormData(e.currentTarget);
      await onSubmit({
        id: safeRandomUUID(),
        name: name.trim(),
        preset,
        protocol: String(form.get("protocol")),
        baseUrl,
        tier,
        endpointCountry: String(form.get("endpointCountry")).toUpperCase(),
        geoPolicyId: String(form.get("geoPolicyId")),
        credential: credential.trim(),
        ...(tier === "paid"
          ? {
              quota: {
                amountLimitUsd: Number(form.get("amountLimitUsd") || 0),
                resetPeriod: "monthly",
                resetTimezone: "Asia/Shanghai",
              },
            }
          : {}),
        attachModel,
        isNewModel,
        targetLogicalModelId: isNewModel ? newModelId.trim() : targetModelId,
        newModelDisplayName: newModelDisplayName.trim() || newModelId.trim(),
        upstreamModelId: upstreamModelId.trim(),
        manualPriority: Number(form.get("manualPriority") || 50),
      });
    } catch (err) {
      setSubmitError(describeError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="form-grid" onSubmit={(e) => void handleSubmit(e)}>
      <label>
        来源名称
        <input
          name="name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="如 NVIDIA Free Pool"
        />
      </label>

      <label>
        供应商预设类型
        <select value={preset} onChange={(e) => handlePresetChange(e.target.value)}>
          <option value="nvidia">NVIDIA NIM</option>
          <option value="openrouter">OpenRouter</option>
          <option value="google">Google Gemini</option>
          <option value="deepseek">DeepSeek</option>
          <option value="moonshot">Moonshot / Kimi</option>
          <option value="custom">Custom (自建 / 兼容端点)</option>
          <option value="amd">AMD Developer Cloud (机会源)</option>
        </select>
      </label>

      {/* 预设感知提示 */}
      {preset === "openrouter" && (
        <div className="preset-callout preset-callout-warning">
          <AlertTriangle size={16} />
          <div>
            <strong>OpenRouter 跨模型回退拦截提示：</strong>
            网关严格执行单一逻辑模型故障切换，客户端传入的 <code>auto</code>、<code>models</code> 等跨模型回退参数将被网关直接剥离或拒绝。
          </div>
        </div>
      )}

      {preset === "google" && (
        <div className="preset-callout preset-callout-info">
          <Info size={16} />
          <div>
            <strong>Gemini 协议兼容性提示：</strong>
            网关将自动向 Gemini 端点注入 <code>x-goog-api-client</code> 请求头，无需在自定义头中额外配置。
          </div>
        </div>
      )}

      {preset === "amd" && (
        <div className="preset-callout preset-callout-warning">
          <Radar size={16} />
          <div>
            <strong>AMD 机会来源说明：</strong>
            AMD Developer Cloud 属于算力机会源，提交后将优先进入候选箱等待管理员人工审核，需配置可用的具体推理端点后才可生效。
          </div>
        </div>
      )}

      <label className="wide">
        端点 Base URL
        <input
          value={baseUrl}
          onChange={(e) => {
            setBaseUrl(e.target.value);
            if (preset === "custom") validateUrl(e.target.value);
          }}
          type="url"
          required
        />
        {urlError && <span style={{ color: "var(--danger)", fontSize: "0.75rem", marginTop: 4 }}>{urlError}</span>}
      </label>

      <label>
        支持协议
        <select name="protocol">
          <option value="chat">Chat Completions (/v1/chat/completions)</option>
          <option value="responses">Responses (/v1/responses)</option>
        </select>
      </label>

      <label>
        额度层级
        <select value={tier} onChange={(e) => setTier(e.target.value as "free" | "paid")}>
          <option value="free">免费层 (最高优先级)</option>
          <option value="paid">付费保底层 (受预算严格约束)</option>
        </select>
      </label>

      <label>
        端点所在国家 (ISO 代码)
        <input name="endpointCountry" required maxLength={7} defaultValue="US" />
        <small>若填写 UNKNOWN，默认不参与调度</small>
      </label>

      <label>
        关联地域合规策略
        <select name="geoPolicyId" required value={geoPolicyId} onChange={(e) => setGeoPolicyId(e.target.value)}>
          <option value="" disabled>请选择与供应商匹配且已核验的策略</option>
          {data.geoPolicies.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>

      {tier === "paid" && (
        <label>
          单源每月预算硬上限 (USD)
          <input name="amountLimitUsd" type="number" min="0" step="0.5" defaultValue="5" />
        </label>
      )}

      <label className="wide">
        上游 API 凭据
        <input
          name="credential"
          type="password"
          required
          value={credential}
          onChange={(e) => setCredential(e.target.value)}
          autoComplete="new-password"
          placeholder="保存后即时 AES-256 加密存盘，前端永不回显明文"
        />
      </label>

      {/* 快捷模型设置与绑定区域 */}
      <div className="preset-callout preset-callout-info wide" style={{ marginTop: 8 }}>
        <Info size={16} />
        <div>
          <strong>模型绑定机制：</strong>
          上游来源提供端点与凭据；逻辑模型对外提供统一调用 ID。您可在此处<strong>一键配置并绑定逻辑模型</strong>，也可稍后在【逻辑模型】与【调度路由】中分别绑定。
        </div>
      </div>

      <label className="checkbox-label wide" style={{ marginTop: 4 }}>
        <input
          type="checkbox"
          checked={attachModel}
          onChange={(e) => setAttachModel(e.target.checked)}
        />
        <span><strong>同时配置并绑定调度模型（推荐）</strong></span>
      </label>

      {attachModel && (
        <>
          <label>
            目标逻辑模型
            <select
              value={isNewModel ? "__new__" : targetModelId}
              onChange={(e) => {
                if (e.target.value === "__new__") {
                  setIsNewModel(true);
                } else {
                  setIsNewModel(false);
                  setTargetModelId(e.target.value);
                }
              }}
            >
              {data.models.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.display_name} ({m.id})
                </option>
              ))}
              <option value="__new__">+ 新建逻辑模型...</option>
            </select>
            <small>对外提供给 OpenCode / 客户端的统一模型标识</small>
          </label>

          <label>
            人工基础优先级 (1-100)
            <input name="manualPriority" type="number" min="1" max="100" defaultValue="50" />
            <small>数字越小越优先，占综合调度评分的 45%</small>
          </label>

          {isNewModel && (
            <>
              <label>
                新逻辑模型 ID
                <input
                  value={newModelId}
                  onChange={(e) => setNewModelId(e.target.value.trim())}
                  required={attachModel && isNewModel}
                  pattern="[a-z0-9._-]+"
                  placeholder="deepseek-v4-flash"
                />
                <small>只能包含小写字母、数字、点、下划线和连字符</small>
              </label>
              <label>
                新模型显示名称
                <input
                  value={newModelDisplayName}
                  onChange={(e) => setNewModelDisplayName(e.target.value)}
                  placeholder="DeepSeek V4 Flash"
                />
                <small>控制台显示友好名称</small>
              </label>
            </>
          )}

          <label className="wide">
            准确上游 Model ID
            <input
              value={upstreamModelId}
              onChange={(e) => setUpstreamModelId(e.target.value.trim())}
              required={attachModel}
              placeholder="如 deepseek-ai/deepseek-v4-flash"
            />
            <small>该供应商实际接受的模型名称。点击下方推荐快捷填充：</small>
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginTop: "6px" }}>
              {getPresetModelSuggestions(preset).map((s) => (
                <button
                  type="button"
                  key={s}
                  className="filter-chip"
                  style={{ fontSize: "0.75rem", padding: "2px 8px" }}
                  onClick={() => {
                    setUpstreamModelId(s);
                    if (isNewModel && (!newModelId || newModelId === "deepseek-v4-flash")) {
                      const inferred = s.split("/").pop() || s;
                      setNewModelId(inferred);
                      setNewModelDisplayName(inferred);
                    }
                  }}
                >
                  {s}
                </button>
              ))}
            </div>
          </label>
        </>
      )}

      {/* 提交失败错误提示 */}
      {submitError && (
        <div
          className="preset-callout preset-callout-warning wide"
          style={{ borderColor: "var(--danger)", color: "var(--danger)", background: "rgba(239, 68, 68, 0.1)" }}
        >
          <AlertTriangle size={16} />
          <div>
            <strong>保存失败：</strong>
            {submitError}
          </div>
        </div>
      )}

      <div className="form-actions">
        {missingRequired && !isSubmitting && (
          <span className="form-hint">
            请填写「来源名称」、「API 凭据」、选择地域策略{attachModel ? "及模型绑定信息" : ""}
          </span>
        )}
        <button type="button" className="ghost-button" onClick={onCancel} disabled={isSubmitting}>
          取消
        </button>
        <button className="primary-button" disabled={missingRequired || Boolean(urlError) || isSubmitting}>
          {isSubmitting ? <RefreshCw size={16} className="spin" /> : <ShieldCheck size={16} />}
          {isSubmitting ? "加密保存中..." : "加密保存 (凭据只写不读)"}
        </button>
      </div>
    </form>
  );
}

// ========================================================
// Helper: KeyCreateForm
// ========================================================
function KeyCreateForm({
  models,
  onCancel,
  onSubmit,
}: {
  models: ModelRow[];
  onCancel: () => void;
  onSubmit: (p: { name: string; rpm: number; concurrency: number; allowedModels: string[] }) => void;
}) {
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    onSubmit({
      name: String(form.get("name")),
      rpm: Number(form.get("rpm")),
      concurrency: Number(form.get("concurrency")),
      allowedModels: form.getAll("allowedModels").map(String),
    });
  };

  return (
    <form className="form-grid" onSubmit={handleSubmit}>
      <label className="wide">
        Key 标识名称
        <input name="name" required placeholder="如 OpenCode 笔记本开发机" />
      </label>
      <label>
        每分钟速率限额 (RPM)
        <input name="rpm" type="number" min="1" defaultValue="60" />
      </label>
      <label>
        最大并发请求租约
        <input name="concurrency" type="number" min="1" defaultValue="4" />
      </label>
      <label className="wide">
        允许访问的模型 (留空代表允许所有启用的逻辑模型)
        <select name="allowedModels" multiple size={Math.min(5, Math.max(2, models.length))}>
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.display_name} ({m.id})
            </option>
          ))}
        </select>
      </label>
      <div className="form-actions">
        <button type="button" className="ghost-button" onClick={onCancel}>
          取消
        </button>
        <button className="primary-button">
          <KeyRound size={16} />
          签发 Gateway Key
        </button>
      </div>
    </form>
  );
}

// ========================================================
// Helper: PolicyEditModal (国家策略管理，带搜索式多选与核验承诺)
// ========================================================
function PolicyEditModal({
  data,
  policyId,
  onClose,
  onSaved,
}: {
  data: AdminData;
  policyId: string;
  onClose: () => void;
  onSaved: (p: GeoPolicyRow) => void;
}) {
  const policy = data.geoPolicies.find((p) => p.id === policyId);
  const [name, setName] = useState(policy?.name ?? "");
  const [restricted, setRestricted] = useState(Boolean(policy?.restricted));
  const [validDays, setValidDays] = useState(policy?.valid_days ?? 30);
  const [policyUrl, setPolicyUrl] = useState(policy?.policy_url ?? "");
  const [allowedReq, setAllowedReq] = useState<string[]>(() =>
    policy ? (JSON.parse(policy.allowed_request_countries_json) as string[]) : [],
  );
  const [countrySearch, setCountrySearch] = useState("");
  const [staleConfirmed, setStaleConfirmed] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  if (!policy) return null;

  const staleDays = Math.floor((Date.now() - policy.verified_at) / 86400000);
  const isStale = staleDays >= policy.valid_days;

  const toggleCountry = (code: string) => {
    setAllowedReq((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const filteredCountries = ISO_COUNTRIES.filter(
    (c) =>
      c.code.toLowerCase().includes(countrySearch.toLowerCase()) ||
      c.name.toLowerCase().includes(countrySearch.toLowerCase()),
  );

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isStale && !staleConfirmed) return;
    const payload = {
      name,
      restricted,
      allowedRequestCountries: allowedReq,
      allowedEndpointCountries: JSON.parse(policy.allowed_endpoint_countries_json) as string[],
      policyUrl: policyUrl || null,
      validDays,
      verifiedAt: Date.now(),
    };
    if (!data.demo) {
      try {
        await mutate(`/geo-policies/${policy.id}`, "PUT", payload);
      } catch (error) {
        // 策略保存失败时保持弹窗打开，避免误以为已刷新核验周期
        setSaveError(describeError(error));
        return;
      }
    }
    onSaved({
      ...policy,
      name: payload.name,
      restricted: payload.restricted ? 1 : 0,
      allowed_request_countries_json: JSON.stringify(payload.allowedRequestCountries),
      policy_url: payload.policyUrl,
      valid_days: payload.validDays,
      verified_at: payload.verifiedAt,
      last_verified_by: "当前管理员",
    });
  };

  return (
    <Modal title={`编辑供应商国家策略: ${policy.name}`} onClose={onClose}>
      <form className="form-grid" onSubmit={(e) => void handleSubmit(e)}>
        {saveError && (
          <div className="preset-callout preset-callout-danger wide">
            <AlertTriangle size={16} />
            <div>
              <strong>保存失败：</strong>
              {saveError}
            </div>
          </div>
        )}
        <label className="wide">
          策略名称
          <input value={name} onChange={(e) => setName(e.target.value)} required />
        </label>

        <label className="checkbox-label wide">
          <input
            type="checkbox"
            checked={restricted}
            onChange={(e) => setRestricted(e.target.checked)}
          />
          <span>启用该供应商严格地域合规限制 (不在允许列表直接返回 451)</span>
        </label>

        <label>
          核验有效天数 (默认 30 天)
          <input
            type="number"
            min="1"
            value={validDays}
            onChange={(e) => setValidDays(Number(e.target.value))}
            required
          />
        </label>

        <label className="wide">
          官方支持国家依据 URL
          <input
            type="url"
            value={policyUrl}
            onChange={(e) => setPolicyUrl(e.target.value)}
            placeholder="https://help.openai.com/..."
          />
        </label>

        {/* 搜索式国家选择 */}
        <div className="wide" style={{ marginTop: "6px" }}>
          <label style={{ marginBottom: 6 }}>
            <strong>允许的请求发起国家 (搜索式勾选，避免手动拼写 ISO 代码)</strong>
          </label>
          <div className="search-input-wrap" style={{ maxWidth: "100%", marginBottom: 8 }}>
            <Search size={14} />
            <input
              placeholder="输入代码或名称筛选 (如 JP, 新加坡, US)..."
              value={countrySearch}
              onChange={(e) => setCountrySearch(e.target.value)}
            />
          </div>
          <div className="country-picker">
            {filteredCountries.map((c) => {
              const selected = allowedReq.includes(c.code);
              return (
                <button
                  type="button"
                  key={c.code}
                  className={`filter-chip ${selected ? "active" : ""}`}
                  onClick={() => toggleCountry(c.code)}
                >
                  {c.code} - {c.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* 强制承诺 */}
        {isStale && (
          <div className="preset-callout preset-callout-warning wide">
            <AlertTriangle size={18} />
            <div>
              <strong>策略已超期核验！</strong>
              <label className="checkbox-label" style={{ marginTop: 6, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={staleConfirmed}
                  onChange={(e) => setStaleConfirmed(e.target.checked)}
                />
                <span>我已重新核对官方政策页面，确认上述国家列表仍完全符合供应商最新条款</span>
              </label>
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="ghost-button" onClick={onClose}>
            取消
          </button>
          <button className="primary-button" disabled={isStale && !staleConfirmed}>
            <ShieldCheck size={16} />
            保存并刷新有效周期
          </button>
        </div>
      </form>
    </Modal>
  );
}
